import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Target, PhoneCall, MessageSquare, Users, DollarSign, TrendingUp, TrendingDown, Check } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";

const stages = [
  { n: "01", icon: Search, t: "Paid Traffic", d: "Meta & Search campaigns designed to capture immediate intent rather than empty clicks. We optimise for pipeline, not impressions." },
  { n: "02", icon: Target, t: "High-Converting Landing Page", d: "One clear page. One clear offer. Every element removes friction. Copy, design and psychology tested until conversion rates lock in." },
  { n: "03", icon: PhoneCall, t: "Automated Booking", d: "Calendar integration with qualification questions that filter out time-wasters — so your calls are with serious buyers only." },
  { n: "04", icon: MessageSquare, t: "WhatsApp & Email Nurture", d: "Automated sequences that follow up with the 80% of leads who aren't ready to buy on day one. Recovering revenue most brands leak." },
  { n: "05", icon: Users, t: "The Value-First Call", d: "A consultative framework that delivers clarity before asking for the sale. Buyers make the decision themselves — with your help." },
  { n: "06", icon: DollarSign, t: "The Invitation", d: "Sales calls become natural invitations rather than high-pressure pitches. Higher close rates, better-fit clients, cleaner cash flow." },
];

const diagnostics = [
  { icon: TrendingDown, k: "Low clicks?", v: "Fix the Ad." },
  { icon: TrendingDown, k: "Low bookings?", v: "Fix the Offer." },
  { icon: TrendingDown, k: "Low show-ups?", v: "Fix the Follow-up / Nurture." },
  { icon: TrendingDown, k: "Low close rate?", v: "Fix the Call Framework." },
  { icon: TrendingUp, k: "All 4 green?", v: "Scale the traffic." },
];

const outcomes = [
  "30+ qualified sales calls / month",
  "Predictable, weekly-tracked revenue pipeline",
  "Automated nurture that recovers the 80% who don't buy day-one",
  "A sales framework you and your team can run without you",
  "Under 90 days to installed — or you don't pay",
];

export default function DigitalSalesEnginePage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-20 md:pt-20 md:pb-24">
          <Reveal>
            <div className="eyebrow mb-6">Digital Sales Engine</div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
              The complete <em className="gold-shine font-medium not-italic">client acquisition system</em>
              <br />
              <span className="text-zinc-300">for </span><span className="text-red-500">high-ticket businesses.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-zinc-400 text-base md:text-lg leading-relaxed font-body">
              Stop guessing. We build and install an end-to-end lead generation and sales engine that
              predictably converts cold traffic into high-ticket clients — with every stage measured,
              tracked and optimised weekly.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" className="btn-primary" data-testid="dse-book-strategy-btn">
                Book a Strategy Call <ArrowUpRight size={18} />
              </Link>
              <a href="#stages" className="btn-ghost" data-testid="dse-see-system-btn">
                See the 6-stage system
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 STAGES */}
      <section id="stages" className="relative py-24 md:py-32 border-t border-white/5 tl-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">The 6-stage system</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              From cold traffic to <em className="gold-shine font-medium not-italic">closed client</em>.
            </h2>
            <p className="mt-6 text-zinc-400 font-body max-w-2xl">
              Every stage is a lever. When one bends, we know exactly which to pull — no guessing, no vibes.
            </p>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stages.map((p, i) => {
              const Icon = p.icon;
              return (
                <StaggerItem key={i}>
                  <div className="luxury-card p-7 h-full" data-testid={`dse-stage-${i}`}>
                    <div className="flex items-center justify-between mb-5">
                      <span className="font-serif-display text-3xl text-red-500">Stage {p.n}</span>
                      <div className="w-10 h-10 bg-red-500/10 border border-red-500/20 flex items-center justify-center" style={{ borderRadius: 0 }}>
                        <Icon size={16} className="text-red-500" />
                      </div>
                    </div>
                    <div className="font-serif-display text-2xl mb-3">{p.t}</div>
                    <p className="text-zinc-400 text-sm font-body leading-relaxed">{p.d}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* DIAGNOSTIC FRAMEWORK */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5">How we measure success</div>
            <h2 className="font-serif-display text-4xl md:text-6xl max-w-3xl leading-[1.05]">
              5 numbers. <em className="gold-shine font-medium not-italic">Every week</em>.
            </h2>
            <p className="mt-6 text-zinc-400 font-body max-w-2xl">
              We eliminate bottlenecks by reading the metrics — not the vibes. Here's the exact diagnostic we run.
            </p>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {diagnostics.map((d, i) => {
              const Icon = d.icon;
              const win = i === 4;
              return (
                <StaggerItem key={i}>
                  <div className={`luxury-card p-6 h-full ${win ? "border-red-500/40" : ""}`} data-testid={`dse-diag-${i}`}>
                    <Icon size={20} className={win ? "text-red-400" : "text-zinc-500"} />
                    <div className="mt-5 font-serif-display text-xl">{d.k}</div>
                    <div className={`mt-2 text-sm ${win ? "text-red-400" : "text-zinc-300"}`}>{d.v}</div>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-alt">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="eyebrow mb-5">Outcomes</div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05]">
                What you actually get.
              </h2>
              <p className="mt-6 text-zinc-400 font-body">
                Not a strategy deck. An installed, running system with a scoreboard you can read every Monday.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-3">
              {outcomes.map((o, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex items-start gap-3 text-zinc-200 text-base font-body">
                    <span className="mt-1.5 w-5 h-5 bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0" style={{ borderRadius: 0 }}>
                      <Check size={12} className="text-red-500" />
                    </span>
                    {o}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 border-t border-white/5 tl-red">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="luxury-card glow-ring relative overflow-hidden p-10 md:p-16">
              <div className="absolute -top-32 -right-32 w-80 h-80 bg-red-500/20 blur-3xl rounded-full pointer-events-none" />
              <div className="eyebrow mb-5">Next step</div>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05]">
                Book a <span className="gold-shine">Strategy Call</span>.
              </h2>
              <p className="mt-6 text-zinc-400 font-body max-w-xl">
                30 minutes with Saurabh. We audit your current acquisition system, spot the leak, and show
                you what a Digital Sales Engine would deliver in your business.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/book" className="btn-primary" data-testid="dse-final-cta">
                  Book a Strategy Call <ArrowUpRight size={18} />
                </Link>
                <Link to="/services/personal-branding" className="btn-ghost" data-testid="dse-see-pb-btn">
                  See Personal Branding
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
